/**
 * Ein modaler Dialog.
 *
 * **Auf dem nativen `<dialog>` und `showModal()`**, nicht auf einem `<div>` mit
 * `position: fixed`. Dieselbe Begründung wie bei `Select` und `Checkbox`: Was
 * der Browser kann, wird nicht nachgebaut. Hier ist die Liste dessen, was man
 * sonst von Hand richtig hinbekommen müsste, besonders lang:
 *
 * - **Fokusfalle.** Der Tabulator bleibt im Dialog, statt hinter ihm durch die
 *   Seite zu wandern.
 * - **Der Rest der Seite wird `inert`** — nicht anklickbar, nicht vorlesbar,
 *   nicht per Tastatur erreichbar. Ein Nachbau lässt einen Screenreader durch
 *   den Inhalt darunter laufen, als wäre nichts.
 * - **Escape schließt.** Das Ereignis heißt `cancel` und wird hier auf
 *   `onClose` gelegt, damit der aufrufende Zustand mitkommt.
 * - **Die oberste Ebene.** Ein `showModal()`-Dialog liegt über allem, ohne dass
 *   jemand `z-index`-Zahlen gegeneinander erhöhen muss.
 *
 * Der einzige Preis: `open` ist keine Eigenschaft, die man einfach setzt — das
 * Element muss die Methoden `showModal()` und `close()` sehen. Das erledigt der
 * Effekt unten, und deshalb ist die Komponente auch nicht nur ein Wrapper.
 */
import { useEffect, useId, useRef, type ReactNode } from "react";
import { fontWeight } from "../tokens.js";
import { IconButton } from "./icon-button.js";
import { IconClose } from "./icons.js";

export interface DialogProps {
  readonly open: boolean;
  /** Wird gerufen, wenn der Dialog schließt — über Escape, das Kreuz oder den Grund daneben. */
  readonly onClose: () => void;
  readonly title: string;
  /** Die Zeile unter dem Titel. */
  readonly description?: string;
  readonly children: ReactNode;
  /** Die Knopfzeile am Fuß. */
  readonly footer?: ReactNode;
  /** Breite des Dialogs. Vorgabe reicht für ein Formular mit einer Spalte. */
  readonly width?: number;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 460,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;

    /**
     * `showModal()` wirft, wenn der Dialog bereits offen ist — etwa nachdem
     * React die Komponente aus einem anderen Grund neu gerendert hat. Die
     * Abfrage auf `open` ist deshalb keine Vorsicht, sondern nötig.
     */
    if (open && !element.open) {
      element.showModal();

      /**
       * **Der Fokus gehört ins erste Eingabefeld, nicht auf das Kreuz.**
       * `showModal()` nimmt das erste fokussierbare Element, und das ist hier
       * der Schließen-Knopf im Kopf — wer den Dialog öffnet, um etwas
       * einzutragen, müsste erst einmal weitertabben. Gibt es kein Feld (ein
       * reiner Bestätigungsdialog), bleibt das native Verhalten.
       *
       * Nicht über `autoFocus`: React setzt das nicht als Attribut, sondern
       * ruft beim Einhängen `focus()` — und eingehängt ist der Dialog schon,
       * bevor er geöffnet wird.
       */
      element.querySelector<HTMLElement>("input, textarea, select")?.focus();
    }

    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      /* Der Titel ist der Name des Dialogs: Ohne die Verknüpfung liest eine Sprachausgabe
         nur „Dialog", und ein Oberflächentest findet ihn nicht beim Namen (#8). */
      aria-labelledby={titleId}
      aria-describedby={description !== undefined ? descriptionId : undefined}
      /* Escape löst `cancel` aus; ohne dieses Weiterreichen bliebe der Zustand
         im Aufrufer auf „offen" stehen, und ein zweiter Klick täte nichts. */
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      /**
       * Ein Klick auf den Grund neben dem Dialog schließt ihn. Das native
       * Element meldet dabei sich selbst als Ziel — der Inhalt liegt in einem
       * Kind, und ein Klick darauf hat ein anderes Ziel.
       */
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      style={{
        width: "min(100% - 2rem, " + width + "px)",
        padding: 0,
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        background: "var(--surface-card)",
        color: "var(--text-body)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <div style={{ padding: "18px 20px 0" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id={titleId}
              style={{
                fontSize: 17,
                fontWeight: fontWeight.bold,
                letterSpacing: "var(--tracking-tight)",
                color: "var(--text-strong)",
              }}
            >
              {title}
            </h2>
            {description !== undefined && (
              <p id={descriptionId} style={{ marginTop: 4, fontSize: 13, color: "var(--text-muted)" }}>
                {description}
              </p>
            )}
          </div>
          <IconButton label="Schließen" variant="ghost" size="sm" onClick={onClose}>
            <IconClose size={16} />
          </IconButton>
        </div>
      </div>

      <div style={{ padding: "16px 20px" }}>{children}</div>

      {footer !== undefined && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "14px 20px",
            borderTop: "1px solid var(--border-subtle)",
            background: "var(--surface-inset)",
            borderBottomLeftRadius: "var(--radius-lg)",
            borderBottomRightRadius: "var(--radius-lg)",
          }}
        >
          {footer}
        </div>
      )}
    </dialog>
  );
}
